import { apiRequest } from "./api.client";

// ─── Types ─────────────────────────────────────────────────────────────────────

export type AppointmentStatus =
  | "requested"
  | "approved"
  | "scheduled"
  | "confirmed"
  | "in_progress"
  | "completed"
  | "cancelled"
  | "no_show";

export type AppointmentSource = "scheduled" | "walk_in";

export type RescheduleReason = "unwell" | "work_conflict" | "transport" | "other";

export interface AppointmentSlot {
  slot_id: string;
  doctor_id: string;
  clinic_id: string;
  start_time: string;
  end_time: string;
  capacity: number;
  booked_count: number;
  is_blocked: boolean;
  created_at: string;
}

export interface Appointment {
  appointment_id: string;
  clinic_id: string;
  patient_id: string;
  doctor_id: string;
  slot_id: string | null; // null for walk-ins (source === "walk_in")
  source: AppointmentSource;
  status: AppointmentStatus;
  notes: string | null;
  cancellation_reason: string | null;
  cancelled_at: string | null;
  rejection_reason: string | null;
  approved_at: string | null;
  approved_by: string | null;
  payment_deadline: string | null;
  rescheduled_to_id: string | null;
  arrived_at: string | null;
  visit_id: string | null;
  booked_by: string | null;
  booking_source: string | null;
  created_at: string;
  updated_at: string;
}

export interface AppointmentListItem {
  appointment_id: string;
  patient_id: string;
  doctor_id: string;
  slot_id: string | null; // null for walk-ins (source === "walk_in")
  source: AppointmentSource;
  status: AppointmentStatus;
  notes: string | null;
  cancellation_reason: string | null;
  rejection_reason: string | null;
  rescheduled_to_id: string | null;
  arrived_at: string | null;
  visit_id: string | null;
  booked_by: string | null;
  booking_source: string | null;
  created_at: string;
}

export interface WaitlistEntry {
  waitlist_id: string;
  clinic_id: string;
  slot_id: string;
  patient_id: string;
  doctor_id: string;
  position: number;
  notes: string | null;
  is_active: boolean;
  added_by: string | null;
  created_at: string;
}

export type BookAppointmentResult = Appointment | WaitlistEntry;

export function isWaitlistEntry(r: BookAppointmentResult): r is WaitlistEntry {
  return "waitlist_id" in r;
}

// ─── Slot create payload ───────────────────────────────────────────────────────

export interface SlotCreatePayload {
  doctor_id: string;
  start_time: string; // ISO
  end_time: string;   // ISO
  capacity?: number;
}

// ─── Appointment create payload ────────────────────────────────────────────────

export interface AppointmentCreatePayload {
  patient_id: string;
  doctor_id: string;
  slot_id: string;
  notes?: string;
}

// ─── API object ───────────────────────────────────────────────────────────────

export interface AppointmentReminder {
  reminder_id: string;
  appointment_id: string;
  channel: "sms" | "whatsapp" | "email";
  status: "pending" | "sent" | "delivered" | "opened" | "failed";
  sent_at: string | null;
  updated_at: string;
}

export type BulkRescheduleSource = "sick_day" | "doctor_block";

export interface SuggestedSlotItem {
  appointment_id: string;
  patient_id: string;
  patient_name: string;
  current_slot: AppointmentSlot;
  suggested_slot: AppointmentSlot | null;
  reason_no_suggestion: string | null;
}

export interface BulkRescheduleSuggestions {
  items: SuggestedSlotItem[];
  /** Every open slot the suggestions were drawn from (through 14 days past
   *  the end of the unavailable period) - the manual-override dropdown's pool. */
  candidate_slots: AppointmentSlot[];
}

export interface BulkRescheduleItemInput {
  appointment_id: string;
  new_slot_id: string;
}

export interface BulkRescheduleFailure {
  appointment_id: string;
  error: string;
}

export interface BulkRescheduleSucceeded {
  appointment_id: string;
  new_appointment_id: string;
  new_slot_id: string;
}

export interface BulkRescheduleResponse {
  succeeded: BulkRescheduleSucceeded[];
  failed: BulkRescheduleFailure[];
  notify_scheduled: boolean;
}

export interface RescheduleHistoryItem {
  history_id: string;
  appointment_id: string;
  patient_id: string;
  old_time: string;
  new_time: string;
  initiator: "patient" | "staff" | "doctor";
  reason: RescheduleReason;
  reason_note: string | null;
  created_at: string;
}

export const AppointmentAPI = {
  // ── Slots ──────────────────────────────────────────────────────────────────
  createSlot: (payload: SlotCreatePayload): Promise<AppointmentSlot> =>
    apiRequest<AppointmentSlot>("/slots", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  getSlot: (id: string): Promise<AppointmentSlot> =>
    apiRequest<AppointmentSlot>(`/slots/${id}`),

  listSlots: (params?: {
    doctor_id?: string;
    date?: string;
    start_date?: string;
    end_date?: string;
    auto_generate?: boolean;
  }): Promise<AppointmentSlot[]> => {
    const qs = new URLSearchParams();
    if (params?.doctor_id) qs.set("doctor_id", params.doctor_id);
    if (params?.date) qs.set("date", params.date);
    if (params?.start_date) qs.set("start_date", params.start_date);
    if (params?.end_date) qs.set("end_date", params.end_date);
    if (params?.auto_generate) qs.set("auto_generate", "true");
    const q = qs.toString();
    return apiRequest<AppointmentSlot[]>(`/slots${q ? `?${q}` : ""}`);
  },

  // ── Appointments CRUD ──────────────────────────────────────────────────────
  book: (payload: AppointmentCreatePayload): Promise<BookAppointmentResult> =>
    apiRequest<BookAppointmentResult>("/appointments", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  list: (params?: {
    doctor_id?: string;
    patient_id?: string;
    date?: string;
    start_date?: string;
    end_date?: string;
    statuses?: string;
  }): Promise<AppointmentListItem[]> => {
    const qs = new URLSearchParams();
    if (params?.doctor_id) qs.set("doctor_id", params.doctor_id);
    if (params?.patient_id) qs.set("patient_id", params.patient_id);
    if (params?.date) qs.set("date", params.date);
    if (params?.start_date) qs.set("start_date", params.start_date);
    if (params?.end_date) qs.set("end_date", params.end_date);
    if (params?.statuses) qs.set("statuses", params.statuses);
    const q = qs.toString();
    return apiRequest<AppointmentListItem[]>(`/appointments${q ? `?${q}` : ""}`);
  },

  get: (id: string): Promise<Appointment> =>
    apiRequest<Appointment>(`/appointments/${id}`),

  // ── Status transitions ─────────────────────────────────────────────────────
  approve: (id: string): Promise<Appointment> =>
    apiRequest<Appointment>(`/appointments/${id}/approve`, { method: "PUT" }),

  reject: (id: string, reason?: string): Promise<Appointment> =>
    apiRequest<Appointment>(`/appointments/${id}/reject`, {
      method: "PUT",
      body: JSON.stringify({ reason: reason || null }),
    }),

  confirm: (id: string): Promise<Appointment> =>
    apiRequest<Appointment>(`/appointments/${id}/confirm`, { method: "PUT" }),

  cancel: (id: string, cancellation_reason: string): Promise<Appointment> =>
    apiRequest<Appointment>(`/appointments/${id}/cancel`, {
      method: "PUT",
      body: JSON.stringify({ cancellation_reason }),
    }),

  reschedule: (
    id: string,
    new_slot_id: string,
    reason: RescheduleReason,
    reason_note?: string,
    override_patient_conflict = false,
  ): Promise<Appointment> =>
    apiRequest<Appointment>(`/appointments/${id}/reschedule`, {
      method: "PUT",
      body: JSON.stringify({ new_slot_id, reason, reason_note, override_patient_conflict }),
    }),

  getRescheduleHistory: (params: { patient_id?: string; appointment_id?: string }): Promise<RescheduleHistoryItem[]> => {
    const qs = new URLSearchParams();
    if (params.patient_id) qs.set("patient_id", params.patient_id);
    if (params.appointment_id) qs.set("appointment_id", params.appointment_id);
    return apiRequest<RescheduleHistoryItem[]>(`/appointments/reschedule-history?${qs.toString()}`);
  },

  getBulkRescheduleSuggestions: (params: {
    source: BulkRescheduleSource;
    doctor_id: string;
    date?: string;
    block_time_id?: string;
  }): Promise<BulkRescheduleSuggestions> => {
    const qs = new URLSearchParams();
    qs.set("source", params.source);
    qs.set("doctor_id", params.doctor_id);
    if (params.date) qs.set("date", params.date);
    if (params.block_time_id) qs.set("block_time_id", params.block_time_id);
    return apiRequest<BulkRescheduleSuggestions>(`/appointments/bulk-reschedule/suggestions?${qs.toString()}`);
  },

  applyBulkReschedule: (payload: {
    source: BulkRescheduleSource;
    doctor_id: string;
    date?: string;
    block_time_id?: string;
    reason_note: string;
    items: BulkRescheduleItemInput[];
    notify: boolean;
  }): Promise<BulkRescheduleResponse> =>
    apiRequest<BulkRescheduleResponse>("/appointments/bulk-reschedule", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  checkin: (id: string): Promise<{ appointment_id: string; status: string; arrived_at: string }> =>
    apiRequest(`/appointments/${id}/checkin`, { method: "POST" }),

  complete: (id: string): Promise<Appointment> =>
    apiRequest<Appointment>(`/appointments/${id}/complete`, { method: "PUT" }),

  noShow: (id: string): Promise<Appointment> =>
    apiRequest<Appointment>(`/appointments/${id}/no-show`, { method: "PUT" }),

  getReminders: (id: string): Promise<AppointmentReminder[]> =>
    apiRequest<AppointmentReminder[]>(`/appointments/${id}/reminders`),

  updateReminderStatus: (
    id: string,
    channel: string,
    status: string,
    sent_at?: string,
  ): Promise<AppointmentReminder> =>
    apiRequest<AppointmentReminder>(`/appointments/${id}/reminders/${channel}`, {
      method: "PUT",
      body: JSON.stringify({ status, sent_at }),
    }),
};